<template>
  <div id="survey-results" class="pa-4">
    <div class="banner-header">
      <div>
        <v-btn
          text
          color="white"
          @click="$router.push('/survey/list')"
          class="mb-2 pl-0"
        >
          <v-icon left>mdi-arrow-left</v-icon> Back to Surveys
        </v-btn>
        <h1 class="page-title">Results</h1>
        <p class="mt-1 mb-0 banner-subtitle">
          {{ TotalResponses }} response(s) collected
        </p>
      </div>
    </div>

    <div class="d-flex justify-content-center mb-6" v-if="LoadingFlag">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
    </div>

    <v-tabs
      v-model="tab"
      background-color="transparent"
      color="#3A67B1"
      slider-color="#3A67B1"
      v-if="!LoadingFlag"
    >
      <v-tab class="font-weight-bold text-capitalize" style="font-size: 14px"
        >Responses</v-tab
      >
      <v-tab class="font-weight-bold text-capitalize" style="font-size: 14px"
        >Summary</v-tab
      >
    </v-tabs>

    <v-tabs-items v-model="tab" v-if="!LoadingFlag">
      <!-- Raw responses -->
      <v-tab-item>
        <v-card class="result-card elevation-4 rounded-xl border-0 mt-4">
          <v-card-text class="pa-2 px-md-4">
            <div class="d-flex justify-end pb-2" v-if="rows.length > 0">
              <v-btn
                color="#3A67B1"
                class="rounded-lg text-white"
                elevation="0"
                @click="
                  generateExcel(
                    rows,
                    excelFields,
                    'survey-responses.xlsx',
                    'Responses'
                  )
                "
              >
                <v-icon left>mdi-download</v-icon> Download
              </v-btn>
            </div>
            <div class="text-center py-10" v-if="rows.length == 0">
              <h4 class="text-muted font-weight-bold">No responses yet.</h4>
            </div>
            <div v-else>
              <v-data-table
                dense
                :headers="tableColumns1"
                :items="rows"
                :items-per-page="25"
                class="neat-survey-table"
              >
                <!-- Submitted date column -->
                <template v-slot:[`item.SubmittedAtTxt`]="{ item }">
                  <span class="text-nowrap">{{
                    item.SubmittedAtTxt || "-"
                  }}</span>
                </template>

                <!-- File upload columns -->
                <template
                  v-for="col in fileColumns"
                  v-slot:[`item.${col}`]="{ item }"
                >
                  <a
                    v-if="item[col]"
                    :key="col"
                    :href="fileBaseUrl + item[col]"
                    target="_blank"
                    rel="noopener"
                    class="text-nowrap"
                    >{{ truncateText(item[col], 20) }}</a
                  >
                  <span v-else :key="col">-</span>
                </template>

                <!-- Question text cells in list -->
                <template
                  v-for="col in textQuestionColumns"
                  v-slot:[`item.${col}`]="{ item }"
                >
                  <div :key="col" class="cell-text-clamp" :title="item[col]">
                    {{ truncateText(item[col], 35) }}
                  </div>
                </template>

                <!-- Preview action icon column -->
                <template v-slot:[`item.Actions`]="{ item }">
                  <div class="text-center">
                    <v-btn
                      icon
                      small
                      color="#3A67B1"
                      @click="openPreview(item)"
                      title="Preview Response"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Per-question summary -->
      <v-tab-item>
        <div class="mt-4">
          <!-- Executive KPI Overview Cards -->
          <v-row class="mb-2">
            <v-col cols="12" sm="6" md="3">
              <v-card
                elevation="2"
                class="rounded-20 pa-4 border-0"
                style="
                  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
                  color: #fff;
                "
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div
                      style="
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        opacity: 0.9;
                        letter-spacing: 0.5px;
                      "
                    >
                      Total Responses
                    </div>
                    <div
                      class="font-weight-bold"
                      style="font-size: 1.6rem; line-height: 1.2"
                    >
                      {{ TotalResponses }}
                    </div>
                  </div>
                  <v-avatar color="rgba(255,255,255,0.2)" size="48">
                    <v-icon color="#FFFFFF" size="26"
                      >mdi-account-group-outline</v-icon
                    >
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card
                elevation="2"
                class="rounded-20 pa-4 border-0"
                style="background-color: #ffffff"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div
                      style="
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        color: #414042;
                        letter-spacing: 0.5px;
                      "
                    >
                      Total Questions
                    </div>
                    <div
                      class="font-weight-bold"
                      style="
                        font-size: 1.6rem;
                        line-height: 1.2;
                        color: #0a0f29;
                      "
                    >
                      {{ summaryQuestions.length }}
                    </div>
                  </div>
                  <v-avatar color="#EBF4FB" size="48">
                    <v-icon color="#3A67B1" size="26"
                      >mdi-help-circle-outline</v-icon
                    >
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card
                elevation="2"
                class="rounded-20 pa-4 border-0"
                style="background-color: #ffffff"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div
                      style="
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        color: #414042;
                        letter-spacing: 0.5px;
                      "
                    >
                      Choice Analytics
                    </div>
                    <div
                      class="font-weight-bold"
                      style="
                        font-size: 1.6rem;
                        line-height: 1.2;
                        color: #0a0f29;
                      "
                    >
                      {{ choiceQuestionsCount }}
                    </div>
                  </div>
                  <v-avatar color="#E6F7F3" size="48">
                    <v-icon color="#56BDA3" size="26">mdi-chart-donut</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card
                elevation="2"
                class="rounded-20 pa-4 border-0"
                style="background-color: #ffffff"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div
                      style="
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        color: #414042;
                        letter-spacing: 0.5px;
                      "
                    >
                      Text Feedback
                    </div>
                    <div
                      class="font-weight-bold"
                      style="
                        font-size: 1.6rem;
                        line-height: 1.2;
                        color: #0a0f29;
                      "
                    >
                      {{ textQuestionsCount }}
                    </div>
                  </div>
                  <v-avatar color="#FDFAF0" size="48">
                    <v-icon color="#EDBE38" size="26"
                      >mdi-comment-text-multiple-outline</v-icon
                    >
                  </v-avatar>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Per Question Cards -->
          <v-card
            v-for="(q, idx) in summaryQuestions"
            :key="q.QuestionId"
            class="result-card elevation-3 rounded-20 border-0 mb-6 overflow-hidden"
            style="background-color: #ffffff"
          >
            <!-- Question Gradient Header Bar -->
            <div
              class="question-gradient-header pa-4 px-6 d-flex align-center justify-space-between flex-wrap"
              style="gap: 10px"
            >
              <div class="d-flex align-center">
                <span class="summary-q-badge-white mr-3 flex-shrink-0">
                  Q{{ idx + 1 }}
                </span>
                <h5
                  class="font-weight-bold mb-0 text-white"
                  style="font-size: 1.05rem; line-height: 1.4"
                >
                  {{ q.QuestionText }}
                </h5>
              </div>

              <div class="d-flex align-center flex-shrink-0" style="gap: 10px">
                <!-- Segmented Chart View Switcher in JCI Seafoam -->
                <div
                  v-if="q.Counts && Object.keys(q.Counts).length > 0"
                  class="chart-segmented-control pa-1 rounded-xl d-flex align-center"
                >
                  <v-btn
                    x-small
                    depressed
                    rounded
                    :color="
                      getChartMode(q.QuestionId, q.Counts) === 'donut'
                        ? '#56BDA3'
                        : 'transparent'
                    "
                    :class="
                      getChartMode(q.QuestionId, q.Counts) === 'donut'
                        ? 'white--text font-weight-bold shadow-xs'
                        : 'text-white-75'
                    "
                    @click="setChartMode(q.QuestionId, 'donut')"
                    title="Donut Chart View"
                  >
                    <v-icon x-small class="mr-1" color="#FFFFFF"
                      >mdi-chart-donut</v-icon
                    >
                    Donut
                  </v-btn>
                  <v-btn
                    x-small
                    depressed
                    rounded
                    :color="
                      getChartMode(q.QuestionId, q.Counts) === 'bar'
                        ? '#56BDA3'
                        : 'transparent'
                    "
                    :class="
                      getChartMode(q.QuestionId, q.Counts) === 'bar'
                        ? 'white--text font-weight-bold shadow-xs'
                        : 'text-white-75'
                    "
                    @click="setChartMode(q.QuestionId, 'bar')"
                    title="Bar Chart View"
                  >
                    <v-icon x-small class="mr-1" color="#FFFFFF"
                      >mdi-chart-bar</v-icon
                    >
                    Bar
                  </v-btn>
                  <v-btn
                    x-small
                    depressed
                    rounded
                    :color="
                      getChartMode(q.QuestionId, q.Counts) === 'progress'
                        ? '#56BDA3'
                        : 'transparent'
                    "
                    :class="
                      getChartMode(q.QuestionId, q.Counts) === 'progress'
                        ? 'white--text font-weight-bold shadow-xs'
                        : 'text-white-75'
                    "
                    @click="setChartMode(q.QuestionId, 'progress')"
                    title="Progress List View"
                  >
                    <v-icon x-small class="mr-1" color="#FFFFFF"
                      >mdi-format-list-bulleted</v-icon
                    >
                    List
                  </v-btn>
                </div>
              </div>
            </div>

            <v-card-text class="pa-6">
              <div v-if="q.Counts">
                <div
                  v-if="Object.keys(q.Counts).length == 0"
                  class="text-muted font-italic py-6 text-center"
                  style="color: #414042"
                >
                  <v-icon color="#3A67B1" size="24" class="mr-1"
                    >mdi-chart-line-variant</v-icon
                  >
                  No responses recorded for this question yet.
                </div>

                <div v-else>
                  <!-- Donut or Bar Chart View -->
                  <v-row
                    align="center"
                    v-if="getChartMode(q.QuestionId, q.Counts) !== 'progress'"
                  >
                    <v-col cols="12" class="text-center">
                      <div
                        class="chart-wrapper pa-2 mx-auto"
                        style="max-width: 680px"
                      >
                        <apexchart
                          v-if="
                            getChartMode(q.QuestionId, q.Counts) === 'donut'
                          "
                          type="donut"
                          :height="getDonutChartHeight(q.Counts)"
                          :options="getDonutChartOptions(q)"
                          :series="getDonutChartSeries(q.Counts)"
                        ></apexchart>
                        <apexchart
                          v-else-if="
                            getChartMode(q.QuestionId, q.Counts) === 'bar'
                          "
                          type="bar"
                          :height="getBarChartHeight(q.Counts)"
                          :options="getBarChartOptions(q)"
                          :series="getBarChartSeries(q.Counts)"
                        ></apexchart>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Progress List View -->
                  <div v-else class="pa-2">
                    <div
                      v-for="(count, option, oIdx) in q.Counts"
                      :key="option"
                      class="mb-3 pa-3 rounded-12 bg-light border"
                    >
                      <div
                        class="d-flex justify-space-between align-center mb-2"
                        style="font-size: 13px"
                      >
                        <span
                          class="font-weight-bold"
                          style="color: #0a0f29; letter-spacing: 0.2px"
                        >
                          {{ option }}
                        </span>
                        <div class="d-flex align-center">
                          <span
                            class="font-weight-bold mr-2"
                            style="color: #0a0f29"
                            >{{ count }} response(s)</span
                          >
                          <v-chip
                            x-small
                            color="#3A67B1"
                            dark
                            class="font-weight-bold"
                          >
                            {{ percentOf(count, q.Counts).toFixed(0) }}%
                          </v-chip>
                        </div>
                      </div>
                      <v-progress-linear
                        :value="percentOf(count, q.Counts)"
                        :color="getOptionColor(oIdx)"
                        height="10"
                        rounded
                        background-color="#FFFFFF"
                        background-opacity="1"
                      ></v-progress-linear>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Text / File Questions Body -->
              <div v-else>
                <div
                  v-if="q.Answers.length == 0"
                  class="text-muted font-italic py-6 text-center"
                  style="color: #414042"
                >
                  <v-icon color="#3A67B1" size="24" class="mr-1"
                    >mdi-comment-text-outline</v-icon
                  >
                  No responses recorded for this question yet.
                </div>

                <div v-else>
                  <!-- Search Bar for Text Responses -->
                  <div
                    class="mb-3 d-flex align-center"
                    v-if="q.Answers.length > 3"
                  >
                    <v-text-field
                      v-model="textSearch[q.QuestionId]"
                      placeholder="Search responses..."
                      dense
                      outlined
                      hide-details
                      prepend-inner-icon="mdi-magnify"
                      class="rounded-lg bg-white"
                      style="max-width: 320px"
                    ></v-text-field>
                    <span class="ml-3 small text-muted">
                      Showing {{ filteredAnswers(q).length }} of
                      {{ q.Answers.length }}
                    </span>
                  </div>

                  <!-- Text Answer List Grid -->
                  <div class="summary-answer-container">
                    <div
                      v-for="(ans, aIdx) in filteredAnswers(q)"
                      :key="aIdx"
                      class="summary-answer-card pa-3 mb-2 rounded-12"
                    >
                      <template v-if="isFileType(q.QuestionType)">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="#3A67B1" class="mr-2"
                              >mdi-file-document-outline</v-icon
                            >
                            <span
                              class="font-weight-medium"
                              style="color: #0a0f29; font-size: 13px"
                              >{{ ans }}</span
                            >
                          </div>
                          <v-btn
                            x-small
                            rounded
                            color="#3A67B1"
                            class="text-white text-capitalize elevation-0"
                            :href="fileBaseUrl + ans"
                            target="_blank"
                            rel="noopener"
                          >
                            <v-icon left x-small color="#FFFFFF"
                              >mdi-open-in-new</v-icon
                            >
                            Download
                          </v-btn>
                        </div>
                      </template>
                      <template v-else>
                        <div class="d-flex align-start">
                          <span class="ans-number mr-2">#{{ aIdx + 1 }}</span>
                          <span class="summary-ans-text flex-grow-1">{{
                            ans
                          }}</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- Preview Response Dialog Modal Component -->
    <survey-result-preview
      v-if="previewDialog"
      v-model="previewDialog"
      :preview-item="previewItem"
      :questions="previewQuestions"
      :file-base-url="fileBaseUrl"
      @hideDialog="previewDialog = false"
    />
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import SurveyResultPreview from "./SurveyResultPreview.vue";

export default {
  mixins: [common],
  components: {
    SurveyResultPreview,
  },
  data() {
    return {
      LoadingFlag: true,
      SurveyId: this.$route.params.id,
      tab: 0,

      rows: [],
      tableColumns1: [],
      questionsMeta: [],
      excelFields: {},
      fileQuestionIds: [],
      fileBaseUrl: "",

      TotalResponses: 0,
      summaryQuestions: [],

      // Interactive Chart & Search State for Summary Tab
      chartModes: {},
      textSearch: {},

      // Preview Modal state
      previewDialog: false,
      previewItem: null,
      previewIndex: 0,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
    };
  },
  watch: {
    PageInfo: function () {
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      if (Object.keys(tr).length > 0) {
        if (!tr.PagePermissionFlag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.getResults();
      }
    },
  },
  computed: {
    choiceQuestionsCount() {
      return (this.summaryQuestions || []).filter((q) => !!q.Counts).length;
    },
    textQuestionsCount() {
      return (this.summaryQuestions || []).filter((q) => !q.Counts).length;
    },
    fileColumns() {
      var ids = this.fileQuestionIds;
      return ids.map(function (id) {
        return "Q" + id;
      });
    },
    textQuestionColumns() {
      var fileCols = this.fileColumns;
      return (this.tableColumns1 || [])
        .map(function (c) {
          return c.value;
        })
        .filter(function (val) {
          return (
            val !== "RespondentTxt" &&
            val !== "SubmittedAtTxt" &&
            val !== "Actions" &&
            !fileCols.includes(val)
          );
        });
    },
    previewQuestions() {
      if (this.questionsMeta && this.questionsMeta.length > 0) {
        return this.questionsMeta;
      }
      var self = this;
      return (this.tableColumns1 || [])
        .filter(function (c) {
          return (
            c.value !== "RespondentTxt" &&
            c.value !== "SubmittedAtTxt" &&
            c.value !== "Actions"
          );
        })
        .map(function (c) {
          var qId = parseInt(c.value.replace("Q", ""), 10);
          return {
            QuestionId: qId,
            QuestionText: c.text,
            QuestionType: self.fileQuestionIds.includes(qId) ? 5 : 1,
          };
        });
    },
  },
  methods: {
    // Get JCI brand color by option index
    getOptionColor(index) {
      var colors = [
        "#3A67B1",
        "#0097D7",
        "#56BDA3",
        "#EDBE38",
        "#414042",
        "#8950FC",
        "#F64E60",
      ];
      return colors[index % colors.length];
    },
    // Calculate total responses for a question
    totalQuestionResponses(q) {
      if (q.Counts) {
        return Object.values(q.Counts).reduce(function (sum, v) {
          return sum + v;
        }, 0);
      }
      if (q.Answers) {
        return q.Answers.length;
      }
      return 0;
    },
    // Get default chart display mode (donut or bar)
    getChartMode(qId, counts) {
      if (this.chartModes[qId]) {
        return this.chartModes[qId];
      }
      var numOpts = Object.keys(counts || {}).length;
      return numOpts <= 5 ? "donut" : "bar";
    },
    // Switch chart display mode for a question
    setChartMode(qId, mode) {
      console.log("setChartMode: Question " + qId + " mode=" + mode);
      this.$set(this.chartModes, qId, mode);
    },
    // Calculate donut chart height
    getDonutChartHeight(counts) {
      var numOpts = Object.keys(counts || {}).length;
      return numOpts > 10 ? 360 : numOpts > 5 ? 320 : 280;
    },

    // Calculate horizontal bar chart height
    getBarChartHeight(counts) {
      var numOpts = Object.keys(counts || {}).length;
      return Math.max(220, numOpts * 30);
    },
    getDonutChartSeries(counts) {
      return Object.values(counts || {});
    },
    getBarChartSeries(counts) {
      return [
        {
          name: "Responses",
          data: Object.values(counts || {}),
        },
      ];
    },
    // Generate donut chart options
    getDonutChartOptions(q) {
      var labels = Object.keys(q.Counts || {});
      return {
        chart: {
          type: "donut",
          toolbar: { show: false },
          fontFamily: "Inter, Roboto, sans-serif",
        },
        labels: labels,
        colors: [
          "#3A67B1",
          "#0097D7",
          "#56BDA3",
          "#EDBE38",
          "#414042",
          "#8950FC",
          "#F64E60",
          "#14B8A6",
          "#F59E0B",
        ],
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(0) + "%";
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total Responses",
                  color: "#414042",
                  fontSize: "12px",
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  },
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " response(s)";
            },
          },
        },
      };
    },
    // Generate horizontal bar chart options
    getBarChartOptions(q) {
      var labels = Object.keys(q.Counts || {});
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "Inter, Roboto, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            barHeight: "50%",
            distributed: true,
          },
        },
        colors: [
          "#3A67B1",
          "#0097D7",
          "#56BDA3",
          "#EDBE38",
          "#414042",
          "#8950FC",
          "#F64E60",
          "#14B8A6",
          "#F59E0B",
        ],
        xaxis: {
          categories: labels,
          labels: {
            style: { colors: "#414042", fontSize: "11px" },
          },
        },
        yaxis: {
          labels: {
            style: { colors: "#0A0F29", fontSize: "12px", fontWeight: 600 },
          },
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          style: { colors: ["#fff"], fontSize: "11px", fontWeight: "bold" },
          formatter: function (val) {
            return val;
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " response(s)";
            },
          },
        },
      };
    },
    // Filter text responses search
    filteredAnswers(q) {
      var query = (this.textSearch[q.QuestionId] || "").toLowerCase().trim();
      var list = q.Answers || [];
      if (!query) return list;
      return list.filter(function (ans) {
        return String(ans).toLowerCase().includes(query);
      });
    },
    truncateText(str, length) {
      if (!str) return "-";
      if (str.length <= length) return str;
      return str.substring(0, length) + "...";
    },
    // Open preview dialog modal
    openPreview(item) {
      console.log("openPreview item=", item);
      this.previewItem = item;
      this.previewDialog = true;
    },
    isFileType(questionType) {
      return questionType == 5 || questionType == 12;
    },
    percentOf(count, counts) {
      var total = Object.values(counts).reduce(function (sum, v) {
        return sum + v;
      }, 0);
      return total > 0 ? (count / total) * 100 : 0;
    },
    // Fetch survey response records and summary analytics
    getResults() {
      console.log("getResults SurveyId=" + this.SurveyId);
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      const thisIns = this;

      this.$http({
        url: server_url + "api/survey/results",
        method: "POST",
        data: { SurveyId: this.SurveyId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }).then(function (response) {
        var records = response.data.records;
        var headers = records.TableHeader || [];

        var qCols = headers.filter(function (h) {
          return (
            h.value !== "RespondentTxt" &&
            h.value !== "SubmittedAtTxt" &&
            h.value !== "Actions"
          );
        });

        headers.forEach(function (h) {
          if (h.value === "RespondentTxt") {
            h.width =
              qCols.length === 2 ? "22%" : qCols.length === 1 ? "28%" : "70%";
          } else if (h.value === "SubmittedAtTxt") {
            h.width = qCols.length === 2 ? "14%" : "18%";
          } else if (h.value === "Actions") {
            h.width = "8%";
            h.align = "center";
            h.sortable = false;
          } else {
            h.width = qCols.length === 2 ? "28%" : "44%";
          }
        });

        thisIns.tableColumns1 = headers;
        thisIns.rows = records.TableData || [];
        thisIns.fileQuestionIds = records.FileQuestionIds || [];
        thisIns.fileBaseUrl = records.FileBaseUrl || "";
        thisIns.questionsMeta = records.Questions || [];

        var fields = {
          Respondent: "RespondentTxt",
          "Submitted At": "SubmittedAtTxt",
        };
        (records.Questions || []).forEach(function (q) {
          fields[q.QuestionText] = "Q" + q.QuestionId;
        });
        thisIns.excelFields = fields;
      });

      this.$http({
        url: server_url + "api/survey/results-summary",
        method: "POST",
        data: { SurveyId: this.SurveyId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          var records = response.data.records;
          thisIns.TotalResponses = records.TotalResponses;
          thisIns.summaryQuestions = records.Questions || [];
        })
        .catch(function () {
          thisIns.LoadingFlag = false;
        });
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = { UserInterface: 1, Module: "survey", Action: "list" };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
  },
  beforeMount() {
    this.refreshPageData();
    this.getResults();
  },
};
</script>

<style lang="scss" scoped>
#survey-results {
  .banner-header {
    background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
    border-radius: 20px;
    padding: 1.5rem 2.5rem;
    margin-bottom: 24px;
  }
  .page-title {
    color: #ffffff;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
  }
  .banner-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
  }
  .result-card {
    border-radius: 20px !important;
    box-shadow: 0 10px 40px rgba(10, 15, 41, 0.05) !important;
  }

  .neat-survey-table {
    ::v-deep table {
      width: 100% !important;
    }
    ::v-deep th {
      white-space: nowrap;
      font-weight: 700 !important;
      font-size: 12px !important;
      background-color: #f8fafc !important;
      color: #1e293b !important;
      padding: 8px 12px !important;
      height: 40px !important;
    }
    ::v-deep td {
      vertical-align: middle;
      font-size: 12px;
      padding: 8px 12px !important;
      height: 38px !important;
    }
  }

  .cell-text-clamp {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    color: #334155;
  }

  .question-gradient-header {
    background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
  }

  .summary-q-badge-white {
    background-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 8px;
    display: inline-block;
    border: 1px solid rgba(255, 255, 255, 0.35);
  }

  .chart-segmented-control {
    background: rgba(10, 15, 41, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(6px);
  }

  .text-white-75 {
    color: rgba(255, 255, 255, 0.85) !important;
    font-weight: 500;
    &:hover {
      color: #ffffff !important;
    }
  }

  .shadow-xs {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
  }

  .summary-q-badge {
    background-color: #ebf4fb;
    color: #3a67b1;
    font-weight: 700;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 8px;
    display: inline-block;
    border: 1px solid #3a67b1;
  }

  .border-navy {
    border: 1px solid #3a67b1 !important;
  }

  .border-bottom {
    border-bottom: 1px solid #e1e8f5 !important;
  }

  .rounded-20 {
    border-radius: 20px !important;
  }

  .rounded-16 {
    border-radius: 16px !important;
  }

  .rounded-12 {
    border-radius: 12px !important;
  }

  .chart-type-toggle {
    background-color: #f4f7fc;
    border: 1px solid #d5e4f7;
  }

  .option-breakdown-card {
    background-color: #f4f7fc;
    border-color: #d5e4f7 !important;
  }

  .option-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }

  .border-bottom-light {
    border-bottom: 1px solid #e1e8f5;
    &:last-child {
      border-bottom: none;
    }
  }

  .ans-number {
    font-size: 11px;
    font-weight: 700;
    color: #3a67b1;
    background-color: #ebf4fb;
    padding: 1px 6px;
    border-radius: 4px;
    display: inline-block;
    margin-top: 2px;
  }

  .summary-answer-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .summary-answer-card {
    background-color: #f4f7fc;
    border-left: 3.5px solid #3a67b1;
    border-radius: 6px;
    border-top: 1px solid #e1e8f5;
    border-right: 1px solid #e1e8f5;
    border-bottom: 1px solid #e1e8f5;
  }

  .summary-ans-text {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.5;
    color: #414042;
  }
}
</style>
